import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Users, Search, FileText, DollarSign, ChevronRight, ChevronDown,
  Mail, Phone, Calendar, Briefcase, Eye, Edit, Save, X, User, Building, Hash, Copy, CreditCard, Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { api } from '../lib/api';
import { toast } from 'sonner';
import PINGate from '../components/PINGate';

const HREmployees = () => {
  const [searchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedDepts, setExpandedDepts] = useState({});
  const [viewMode, setViewMode] = useState('department'); // 'department' or 'list'
  const searchTimeoutRef = useRef(null);

  // URL params for filtering
  const filterDept = searchParams.get('department');
  const filterRole = searchParams.get('role');
  const highlightId = searchParams.get('highlight');

  // Profile form with bank details
  const [profileForm, setProfileForm] = useState({
    employee_code: '',
    employee_name: '',
    designation: '',
    department: '',
    personal_email: '',
    phone_number: '',
    joining_date: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_name: ''
  });

  useEffect(() => {
    if (pinVerified) {
      fetchData();
    }
  }, [pinVerified]);

  useEffect(() => {
    // Auto-expand department from URL
    if (filterDept && departments.length > 0) {
      setExpandedDepts({ [filterDept]: true });
    }
  }, [filterDept, departments]);

  // Debounced search term update
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 300);
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, deptRes] = await Promise.all([
        api.getHREmployees(),
        api.getDepartments()
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      
      // Expand all departments initially
      const expanded = {};
      deptRes.data.forEach(d => expanded[d.name] = true);
      expanded['Unassigned'] = true;
      setExpandedDepts(expanded);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetail = async (employee) => {
    try {
      const response = await api.getHREmployee(employee.id);
      setSelectedEmployee(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load employee details');
    }
  };

  const openEditModal = (employee) => {
    setSelectedEmployee(employee);
    setProfileForm({
      employee_code: employee.profile?.employee_code || '',
      employee_name: employee.profile?.employee_name || employee.name || '',
      designation: employee.profile?.designation || '',
      department: employee.profile?.department || '',
      personal_email: employee.profile?.personal_email || '',
      phone_number: employee.profile?.phone_number || '',
      joining_date: employee.profile?.joining_date || '',
      bank_account_number: employee.profile?.bank_account_number || '',
      bank_ifsc: employee.profile?.bank_ifsc || '',
      bank_name: employee.profile?.bank_name || ''
    });
    setShowEditModal(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.updateEmployeeProfile(selectedEmployee.id, profileForm);
      toast.success('Employee profile updated');
      setShowEditModal(false);
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Filter employees using debounced search term
  let filteredEmployees = employees.filter(emp => 
    emp.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    emp.role?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    emp.profile?.employee_code?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    emp.profile?.designation?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    emp.profile?.phone_number?.includes(debouncedSearchTerm)
  );

  if (filterDept) {
    filteredEmployees = filteredEmployees.filter(emp => 
      emp.profile?.department === filterDept || (!emp.profile?.department && filterDept === 'Unassigned')
    );
  }
  if (filterRole) {
    filteredEmployees = filteredEmployees.filter(emp => emp.role === filterRole);
  }

  // Group by department
  const groupedByDept = {};
  filteredEmployees.forEach(emp => {
    const dept = emp.profile?.department || 'Unassigned';
    if (!groupedByDept[dept]) groupedByDept[dept] = [];
    groupedByDept[dept].push(emp);
  });

  const getRoleBadgeColor = (role) => {
    const colors = {
      'admin': 'bg-red-100 text-red-800',
      'manager': 'bg-purple-100 text-purple-800',
      'caller': 'bg-blue-100 text-blue-800',
      'teacher': 'bg-green-100 text-green-800',
      'stock_manager': 'bg-cyan-100 text-cyan-800',
      'hr_admin': 'bg-indigo-100 text-indigo-800',
      'hr_manager': 'bg-indigo-100 text-indigo-800',
      'finance_admin': 'bg-amber-100 text-amber-800',
    };
    return colors[role] || 'bg-slate-100 text-slate-800';
  };

  const toggleDept = (dept) => {
    setExpandedDepts(prev => ({ ...prev, [dept]: !prev[dept] }));
  };

  const EmployeeRow = ({ employee }) => (
    <div 
      className={`py-3 flex items-center justify-between hover:bg-slate-50 px-4 cursor-pointer transition-colors ${
        highlightId === employee.id ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''
      }`}
      onClick={() => fetchEmployeeDetail(employee)}
      data-testid={`employee-row-${employee.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
          <span className="text-sm font-semibold text-indigo-600">
            {(employee.profile?.employee_name || employee.name)?.charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <div className="font-medium text-slate-900">
            {employee.profile?.employee_name || employee.name}
            {employee.profile?.employee_code && (
              <span className="ml-2 text-xs text-slate-500">({employee.profile.employee_code})</span>
            )}
          </div>
          <div className="text-sm text-slate-500 flex items-center gap-2">
            {employee.profile?.designation || employee.role}
            {employee.profile?.phone_number ? (
              <span className="flex items-center gap-1 text-emerald-600">
                <Phone className="h-3 w-3" /> {employee.profile.phone_number}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-orange-500 text-xs">
                <Phone className="h-3 w-3" /> No phone
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={getRoleBadgeColor(employee.role)}>
          {employee.role?.replace(/_/g, ' ')}
        </Badge>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </div>
    </div>
  );

  const EmployeesContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Employees</h1>
            <p className="text-slate-500">
              {filterDept ? `Department: ${filterDept}` : 'Manage employee records and information'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={viewMode === 'department' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('department')}
            >
              <Building2 className="h-4 w-4 mr-1" /> By Department
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <Users className="h-4 w-4 mr-1" /> List View
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, code, phone, designation..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
                data-testid="employee-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Department-wise View */}
        {viewMode === 'department' ? (
          <div className="space-y-4">
            {Object.keys(groupedByDept).sort().map(dept => (
              <Card key={dept}>
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                  onClick={() => toggleDept(dept)}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-indigo-500" />
                    <span className="font-semibold text-slate-900">{dept}</span>
                    <Badge variant="outline">{groupedByDept[dept].length}</Badge>
                  </div>
                  {expandedDepts[dept] ? (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  )}
                </div>
                {expandedDepts[dept] && (
                  <CardContent className="pt-0 divide-y divide-slate-100">
                    {groupedByDept[dept].map(employee => (
                      <EmployeeRow key={employee.id} employee={employee} />
                    ))}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        ) : (
          /* List View */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-slate-500" />
                  Employee Directory
                </span>
                <Badge variant="outline">{filteredEmployees.length} employees</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-slate-100">
              {filteredEmployees.map(employee => (
                <EmployeeRow key={employee.id} employee={employee} />
              ))}
              {filteredEmployees.length === 0 && (
                <div className="py-8 text-center text-slate-500">
                  No employees found matching your criteria
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Employee Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedEmployee && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Employee Name</Label>
                    <div className="font-medium">{selectedEmployee.profile?.employee_name || selectedEmployee.name}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Employee Code</Label>
                    <div className="font-medium">{selectedEmployee.profile?.employee_code || 'Not set'}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Designation</Label>
                    <div className="font-medium">{selectedEmployee.profile?.designation || selectedEmployee.role}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Department</Label>
                    <div className="font-medium">{selectedEmployee.profile?.department || 'Unassigned'}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Email</Label>
                    <div className="font-medium">{selectedEmployee.profile?.personal_email || selectedEmployee.email}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <div className="font-medium flex items-center gap-2">
                      {selectedEmployee.profile?.phone_number || (
                        <span className="text-orange-500 italic">Not set - Please update!</span>
                      )}
                      {selectedEmployee.profile?.phone_number && (
                        <>
                          <button 
                            onClick={() => copyToClipboard(selectedEmployee.profile.phone_number, 'Phone')}
                            className="p-1 hover:bg-slate-200 rounded"
                          >
                            <Copy className="h-3 w-3 text-slate-500" />
                          </button>
                          {selectedEmployee.profile?.phone_from_user && (
                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                              From User Profile
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Joining Date</Label>
                    <div className="font-medium">{selectedEmployee.profile?.joining_date || 'Not set'}</div>
                  </div>
                </div>

                {/* Bank Details Section */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> Bank Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Label className="text-xs text-blue-600">Account Number</Label>
                      <div className="font-medium flex items-center gap-2">
                        {selectedEmployee.profile?.bank_account_number || 'Not set'}
                        {selectedEmployee.profile?.bank_account_number && (
                          <button 
                            onClick={() => copyToClipboard(selectedEmployee.profile.bank_account_number, 'Account Number')}
                            className="p-1 hover:bg-blue-100 rounded"
                            data-testid="copy-account-btn"
                          >
                            <Copy className="h-3 w-3 text-blue-500" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Label className="text-xs text-blue-600">IFSC Code</Label>
                      <div className="font-medium flex items-center gap-2">
                        {selectedEmployee.profile?.bank_ifsc || 'Not set'}
                        {selectedEmployee.profile?.bank_ifsc && (
                          <button 
                            onClick={() => copyToClipboard(selectedEmployee.profile.bank_ifsc, 'IFSC')}
                            className="p-1 hover:bg-blue-100 rounded"
                          >
                            <Copy className="h-3 w-3 text-blue-500" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Label className="text-xs text-blue-600">Bank Name</Label>
                      <div className="font-medium">{selectedEmployee.profile?.bank_name || 'Not set'}</div>
                    </div>
                  </div>
                </div>

                {/* Documents Summary */}
                {selectedEmployee.documents && selectedEmployee.documents.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Documents ({selectedEmployee.documents.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmployee.documents.map(doc => (
                        <Badge key={doc.id} variant="outline" className="py-1">
                          {doc.document_type?.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button onClick={() => { setShowDetailModal(false); openEditModal(selectedEmployee); }}>
                <Edit className="h-4 w-4 mr-2" /> Edit Profile
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Employee Profile</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Employee Code</Label>
                  <Input
                    value={profileForm.employee_code}
                    onChange={(e) => setProfileForm({...profileForm, employee_code: e.target.value})}
                    placeholder="EMP001"
                  />
                </div>
                <div>
                  <Label>Employee Name</Label>
                  <Input
                    value={profileForm.employee_name}
                    onChange={(e) => setProfileForm({...profileForm, employee_name: e.target.value})}
                    placeholder="Full Name"
                  />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input
                    value={profileForm.designation}
                    onChange={(e) => setProfileForm({...profileForm, designation: e.target.value})}
                    placeholder="e.g., Senior Developer"
                  />
                </div>
                <div>
                  <Label>Department</Label>
                  <select
                    value={profileForm.department}
                    onChange={(e) => setProfileForm({...profileForm, department: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="">Select Department</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Personal Email</Label>
                  <Input
                    type="email"
                    value={profileForm.personal_email}
                    onChange={(e) => setProfileForm({...profileForm, personal_email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={profileForm.phone_number}
                    onChange={(e) => setProfileForm({...profileForm, phone_number: e.target.value})}
                    placeholder="9876543210"
                  />
                </div>
                <div>
                  <Label>Joining Date</Label>
                  <Input
                    type="date"
                    value={profileForm.joining_date}
                    onChange={(e) => setProfileForm({...profileForm, joining_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Bank Details */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Bank Details
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={profileForm.bank_account_number}
                      onChange={(e) => setProfileForm({...profileForm, bank_account_number: e.target.value})}
                      placeholder="Account Number"
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={profileForm.bank_ifsc}
                      onChange={(e) => setProfileForm({...profileForm, bank_ifsc: e.target.value})}
                      placeholder="IFSC Code"
                    />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={profileForm.bank_name}
                      onChange={(e) => setProfileForm({...profileForm, bank_name: e.target.value})}
                      placeholder="Bank Name"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  return (
    <PINGate 
      module="hr" 
      onVerified={() => setPinVerified(true)}
      title="HR Module"
    >
      <EmployeesContent />
    </PINGate>
  );
};

export default HREmployees;
